// State-specific landing — form + role list side-by-side (form first on mobile).
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, Briefcase, Flame, MapPin, Lock, ChevronDown, Loader2, Check } from "lucide-react";
import { API } from "../lib/api";
import usePriorityStates from "../hooks/usePriorityStates";
import { fieldCls } from "../components/QuickMatchForm";
import { submitApplication, isValidEmail, isValidPhone } from "../lib/apply";

function RoleRow({ job, selected, local, onPick }) {
  return (
    <button type="button" onClick={() => onPick(job)} className={`w-full text-left rounded-xl bg-white p-4 ring-1 transition-shadow flex items-center gap-3 sm:gap-4 ${selected ? "ring-2 ring-[#0F2A1F]" : "ring-[#0F2A1F]/10 hover:ring-[#0F2A1F]/30"}`} data-testid={`state-job-${job.id}`}>
      <div className="h-10 w-10 rounded-lg bg-[#0F2A1F] flex items-center justify-center shrink-0"><Briefcase className="h-4 w-4 text-[#D4AF37]" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[10px] font-semibold">
          <span className="rounded-full bg-[#10B981]/15 text-[#0d7a55] px-2 py-0.5">Remote</span>
          {local && <span className="rounded-full bg-[#D4AF37]/25 text-[#7a5f12] px-2 py-0.5">{job.state_label} residents</span>}
        </div>
        <div className="mt-1 font-bold text-[#0F2A1F] truncate">{job.title}</div>
        <div className="mt-0.5 text-xs text-[#0F2A1F]/60 truncate">{job.salary_range} · {job.experience}</div>
      </div>
      <div className={`shrink-0 h-8 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 ${selected ? "bg-[#0F2A1F] text-white" : "bg-[#FAF7EE] text-[#0F2A1F]"}`}>
        {selected ? <><Check className="h-3.5 w-3.5" /> Selected</> : "Select"}
      </div>
    </button>
  );
}

export default function ApplyState() {
  const { code } = useParams();
  const stateCode = (code || "").toUpperCase();
  const nav = useNavigate();
  const meta = usePriorityStates();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", note: "", target_role: "" });
  const [cv, setCv] = useState(null);
  const [extras, setExtras] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/jobs?state=${stateCode}`).then((r) => r.json()).then((rows) => { setJobs(rows || []); setLoading(false); }).catch(() => setLoading(false));
  }, [stateCode]);

  const stateLabel = meta.all_states.find((s) => s.code === stateCode)?.label || stateCode;
  const isHigh = meta.high_priority.some((s) => s.code === stateCode);
  const isPriority = meta.priority.some((s) => s.code === stateCode);
  const tierLabel = isHigh ? "Top priority · fast-tracked" : isPriority ? "Priority state · hiring actively" : "Now hiring in your state";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const chooseJob = (job) => {
    setSelectedJobId(job.id);
    setForm((f) => ({ ...f, target_role: job.title }));
    document.getElementById("state-apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your name.");
    if (!isValidEmail(form.email)) return toast.error("Please enter a valid email.");
    if (!isValidPhone(form.phone)) return toast.error("Please enter a valid phone number.");
    const role = form.target_role.trim() || `Any remote role · ${stateLabel}`;
    setBusy(true);
    try {
      const res = await submitApplication({ ...form, target_role: role, job_id: selectedJobId, state: stateCode }, cv);
      nav("/thank-you", { state: { role, name: form.full_name, tier: res.priority_tier, appId: res.id } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="pt-24 md:pt-28 pb-20 min-h-screen bg-[#FAF7EE]" data-testid="apply-state-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-[#0F2A1F]/70 hover:text-[#0F2A1F]" data-testid="apply-state-back"><ArrowLeft className="h-4 w-4" /> Back</button>

        <div className="relative overflow-hidden mt-3 rounded-2xl bg-[#0F2A1F] text-white p-6 md:p-12" data-testid="apply-state-hero">
          <div aria-hidden className="absolute inset-0 hero-glow" />
          <div className="relative">
            <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-semibold">
              {isHigh ? <Flame className="h-4 w-4 text-[#D4AF37]" /> : <MapPin className="h-4 w-4 text-[#D4AF37]" />}
              <span className="text-[#D4AF37]">{tierLabel}</span>
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">Remote jobs for <span className="text-[#D4AF37] italic">{stateLabel}</span>.</h1>
            <p className="mt-3 text-base md:text-lg text-white/70 max-w-2xl">Work from home, get paid like a local. Fill in your details, pick a role (or don't) — a recruiter gets back to you within 2 business days.</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <form id="state-apply-form" onSubmit={submit} noValidate className="lg:col-span-5 min-w-0 scroll-mt-24 rounded-2xl bg-white p-5 sm:p-7 ring-1 ring-[#0F2A1F]/10 shadow-[0_24px_60px_-35px_rgba(15,42,31,0.5)] space-y-3 lg:sticky lg:top-24" data-testid="state-apply-form">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0F2A1F]">Apply in 30 seconds</h2>
            <p className="text-sm text-[#0F2A1F]/60 !mt-1">Location set to <strong className="text-[#0F2A1F]">{stateLabel}</strong>. No account needed.</p>
            <input className={fieldCls} placeholder="Full name" autoComplete="name" value={form.full_name} onChange={set("full_name")} data-testid="state-apply-name" />
            <input className={fieldCls} type="email" inputMode="email" placeholder="Email address" autoComplete="email" value={form.email} onChange={set("email")} data-testid="state-apply-email" />
            <input className={fieldCls} type="tel" inputMode="tel" placeholder="Phone number" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="state-apply-phone" />
            <input className={fieldCls} placeholder="Role (pick below, type one, or leave blank)" value={form.target_role} onChange={(e) => { set("target_role")(e); setSelectedJobId(""); }} data-testid="state-apply-role" />
            <button type="button" onClick={() => setExtras((v) => !v)} className="w-full flex items-center justify-between px-1 py-2 text-sm font-semibold text-[#0F2A1F]" data-testid="state-apply-extras-toggle">
              <span>Add CV or a note <span className="font-normal text-[#0F2A1F]/50">(optional)</span></span>
              <ChevronDown className={`h-4 w-4 transition-transform ${extras ? "rotate-180" : ""}`} />
            </button>
            <div className={extras ? "space-y-3" : "hidden"}>
              <input ref={fileRef} type="file" accept="application/pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="state-apply-cv-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full rounded-lg bg-[#FAF7EE] ring-1 ring-[#0F2A1F]/15 hover:ring-[#0F2A1F]/40 p-3.5 flex items-center gap-3 text-left" data-testid="state-apply-cv-btn">
                <Upload className="h-4 w-4 text-[#0F2A1F]/60" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0F2A1F] text-sm truncate">{cv ? cv.name : "Upload your CV"}</div>
                  <div className="text-xs text-[#0F2A1F]/55">{cv ? `${Math.round(cv.size / 1024)} KB` : "PDF or DOCX · max 8MB"}</div>
                </div>
              </button>
              <textarea rows={3} className={`${fieldCls} h-auto py-3`} value={form.note} onChange={set("note")} placeholder="Availability, salary expectations…" data-testid="state-apply-note" />
            </div>
            <button type="submit" disabled={busy} className="w-full h-14 rounded-xl bg-[#D4AF37] text-[#0F2A1F] text-base font-bold inline-flex items-center justify-center gap-2 hover:bg-[#e2bf48] active:scale-[0.99] transition-[background-color,transform] disabled:opacity-70" data-testid="state-apply-submit">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send my application <ArrowRight className="h-5 w-5" /></>}
            </button>
            <p className="flex items-start gap-1.5 text-xs text-[#0F2A1F]/55"><Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Free for job seekers. We only share your profile with employers in our network.</p>
          </form>

          <div className="lg:col-span-7 min-w-0">
            <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-2xl font-bold text-[#0F2A1F]">Open roles for {stateLabel}</h2>
              <div className="text-sm text-[#0F2A1F]/55">{jobs.length} open</div>
            </div>
            {loading ? (
              <div className="flex justify-center py-12 text-[#0F2A1F]/40"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
              <div className="space-y-2.5" data-testid="state-jobs-list">
                {jobs.map((j) => <RoleRow key={j.id} job={j} local={j.state === stateCode} selected={j.id === selectedJobId} onPick={chooseJob} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
